import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createFormatters, type Formatters, type FormattersConfig } from './formatters';

const FormattersContext = createContext<Formatters | null>(null);

let fallbackFormatters: Formatters | undefined;

export interface FormattersProviderProps extends FormattersConfig {
  children?: ReactNode;
}

/**
 * Supplies the viewer's formatters. Put it once under the session so `useFormatters()` anywhere below
 * shows dates in their zone, numbers in their locale and weights in their unit family.
 */
export function FormattersProvider({ locale, timeZone, units, currencyDisplay, children }: FormattersProviderProps) {
  const formatters = useMemo(
    () => createFormatters({ locale, timeZone, units, currencyDisplay }),
    [locale, timeZone, units, currencyDisplay],
  );
  return <FormattersContext.Provider value={formatters}>{children}</FormattersContext.Provider>;
}

/** The formatters in scope; English, metric, browser zone when there is no provider. */
export function useFormatters(): Formatters {
  const formatters = useContext(FormattersContext);
  if (formatters) return formatters;
  fallbackFormatters ??= createFormatters();
  return fallbackFormatters;
}
